/**
 * TrackPage Component
 */

import * as playerActions from '../../../redux/actions/player';
import { usageToString } from '../../../redux/helpers/license';
import { getPlayerTracksFromResults } from '../../../redux/helpers/player';
import { getTermsByTaxonomy, TaxonomyType } from '../../../relay/selectors/taxonomy';
import { TrackFragment } from '../../../relay/track';
import { getFormattedTime } from '../../../utils/date';
import { getResponsiveAssets } from '../../../utils/image';
import { capitalizeString, stripHTMLTagsFromString } from '../../../utils/string';
import {
  canLicenseTrack,
  getArtistPagePath,
  getDefaultTrackAsset,
  getResponsiveTrackThumbnail,
  getTrackAssets,
} from '../../../utils/track';
import { bw7 } from '../../styles/colors';
import './styles.scss';

import { RouteMatch } from 'found';
import { isEqual } from 'lodash';
import throttle from 'raf-throttle';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import { createFragmentContainer } from 'react-relay';

import Page from '../../../containers/Page';
import withLicenseBar from '../../../containers/withLicenseBar';
import { IQueryStringContext, QueryStringContext } from '../../../contexts/queryString';
import AddToCartButton from '../../atoms/buttons/AddToCartButton';
import AddToProjectButton from '../../atoms/buttons/AddToProjectButton';
import Button, { ButtonProps } from '../../atoms/buttons/Button';
import CardPlayButton from '../../atoms/buttons/CardPlayButton';
import DownloadButton from '../../atoms/buttons/DownloadButton';
import SessionLicenseBuilderButton from '../../atoms/buttons/LicenseBuilderButton/SessionLicenseBuilderButton';
import LinkButtonWithLicense from '../../atoms/buttons/LinkButtonWithLicense';
import {
  IconAddCircle,
  IconAddToCart,
  IconArrowRight,
  IconDownload,
  IconEdit,
  IconMusicNote,
  IconTerms,
} from '../../atoms/images/Icon';
import Picture from '../../atoms/images/Picture';
import Thumbnail from '../../atoms/images/Thumbnail';
import WaveformPlaceholder from '../../atoms/images/WaveformPlaceholder';
import Heading from '../../atoms/text/Heading';
import { Span } from '../../atoms/text/Label';
import PriceTag from '../../atoms/text/PriceTag';
import DropdownMenu from '../../molecules/blocks/DropdownMenu';
import ThumbnailWithPlayButton from '../../molecules/buttons/ThumbnailWithPlayButton';
import Tag from '../../molecules/Tag';
import Collections from '../../organisms/content/Collections';
import { TrackListQuery } from '../../organisms/search/TrackList';
import { ArtistPagePath } from '../Artist';
import TrackHelmet from './helmet';

// Page Path
export const TrackPagePath = (slug: string) => `/track/${slug}`;

// Page Props
interface IOwnStateProps {
  currentTrackPlaying: string;
  currentVersionPlaying: string;
  licenseUsages: ILicenseUsage[];
}

interface IOwnDispatchProps {
  pausePlayer(): void;
  playTrackVersion(versionId: string): void;
  setPlayerQueue(tracks: IPlayerTrack[]): void;
}

interface IOwnProps extends RouteMatch {
  relay: IRelayProps<ITrackSearchQueryVariables>;
  track: GQL.ITrackSearchResult;
}

type QueryStringContextProps = Pick<IQueryStringContext, 'addSearchFilter' | 'addTempoFilter'>;

export type TrackPageProps =
  & IOwnProps
  & IOwnStateProps
  & IOwnDispatchProps
  & QueryStringContextProps;

interface IOwnState {
  downloadMenuOpen: boolean;
  headerIsSticky?: boolean;
  selectedVersion: string;
}

const classes = new BEM('track');
const defaultList = [];

class TrackPage extends React.Component<TrackPageProps, IOwnState> {
  private artistImage: IResponsiveImage;
  private coverImage: IResponsiveImage;
  private originalVersionId: string;
  private terms: GQL.ITaxonomyTerm[];
  private versions: GQL.IAsset[];

  private onScrollThrottled;
  private hero: HTMLElement;
  private heroBackground: HTMLElement;

  constructor(props: TrackPageProps) {
    super(props);
    const { track } = props;

    this.artistImage = getResponsiveAssets('cover', track.artist!.assets || defaultList);
    this.coverImage = getResponsiveTrackThumbnail(track);
    this.terms = track.terms!.edges!.map(({ node }) => node!);

    if (track.assets) {
      const defaultTrack = getDefaultTrackAsset(track.assets);
      this.originalVersionId = defaultTrack ? defaultTrack.id : '';
      this.versions = getTrackAssets({ assets: track.assets, format: 'mp3', type: 'track-preview' });
    }

    this.onScrollThrottled = throttle(this.onScroll);

    this.state = {
      downloadMenuOpen: false,
      selectedVersion: this.originalVersionId,
    };
  }

  componentDidMount() {
    if (this.props.track) {
      this.props.setPlayerQueue(getPlayerTracksFromResults([this.props.track]));
    }

    const el = findDOMNode(this) as HTMLElement;
    this.hero = el.getElementsByClassName('track__hero')[0] as HTMLElement;
    this.heroBackground = el.getElementsByClassName('track__artist-picture')[0] as HTMLElement;

    window.addEventListener('scroll', this.onScrollThrottled);
  }

  shouldComponentUpdate(nextProps: TrackPageProps, nextState: IOwnState) {
    if (isEqual(nextProps, this.props) && isEqual(nextState, this.state)) {
      return false;
    }

    return true;
  }

  componentDidUpdate(prevProps: TrackPageProps, prevState: IOwnState) {
    // Whenever user toggles between versions, playback starts
    if (prevState.selectedVersion !== this.state.selectedVersion) {
      this.props.pausePlayer();
      this.props.playTrackVersion(this.state.selectedVersion);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScrollThrottled);
    this.onScrollThrottled = undefined;
  }

  onClickVersion = (id: string) => () => {
    this.setState({ selectedVersion: id });
  }

  onSetTempoFilter = (value: number) => {
    this.props.addTempoFilter({
      minimum: value,
      maximum: value,
    });
  }

  onToggleDropdownMenu = (downloadMenuOpen: boolean) => {
    this.setState({ downloadMenuOpen });
  }

  onScroll = () => {
    this.heroBackground.style.transform = `translate3d(0, -${window.scrollY * .1}px, 0)`;
    this.hero.style.transform = `translate3d(0, -${window.scrollY * .2}px, 0)`;
  }

  renderHero() {
    const {
      currentVersionPlaying,
      track,
    } = this.props;

    return (
      <div {...classes('hero')}>
        <Picture assets={this.artistImage} {...classes('artist-picture')} />
        <div {...classes('hero__content')}>
          <ThumbnailWithPlayButton
            trackId={currentVersionPlaying || this.originalVersionId}
            responsiveAssets={this.coverImage}
            size="large"
            {...classes('cover-image')}
          />
          <Heading
            is="h2"
            text={stripHTMLTagsFromString(track.title!)}
            {...classes('title')}
          />
          <Heading
            is="h6"
            hyperLinkProps={{
              href: getArtistPagePath(track),
            }}
            text={stripHTMLTagsFromString(track.artist!.name!)}
            {...classes('artist')}
          />
          {this.versions.length > 1 &&
            <div {...classes('versions')}>
              <div className="buttons-group">
                {this.versions.map(({ id, version }) => {
                  const isActive = this.state.selectedVersion === id;
                  const extraClasses = {
                    'is-active': isActive,
                  };
                  const buttonProps: Partial<ButtonProps> = {
                    label: capitalizeString(version!),
                    ...classes('version-button', '', extraClasses),
                  };
                  if (!isActive) {
                    buttonProps.onClick = this.onClickVersion(id);
                  }
                  return <Button key={id} {...buttonProps} />;
                })}
              </div>
            </div>
          }
        </div>
      </div>
    );
  }

  renderHeader(sticky?: boolean) {
    const { track } = this.props;
    const downloadableAsset = getDefaultTrackAsset(track.assets!);
    const versions = getTrackAssets({ assets: track.assets || defaultList, format: 'mp3', type: 'track-preview' });
    return (
      <div {...classes('header')}>
        <div {...classes('details')}>
          <Thumbnail
            {...classes('cover-image')}
            size="xsmall"
            responsiveAssets={this.coverImage}
          />
          <div {...classes('info')}>
            <Heading is="h5" {...classes('info__title')}>{track.title!}</Heading>
            <Span
              text={track.artist!.name!}
              size="small"
              textColor="bw7"
              {...classes('info__subtitle')}
            />
          </div>
        </div>
        <div {...classes('header__actions')}>
          <AddToProjectButton trackId={track.id} {...classes('action')} />
          {versions.length > 1 ? (
            <DropdownMenu
              Button={(
                <Button
                  icon={<IconDownload />}
                  label="Download Preview MP3"
                  {...classes('action')}
                />
              )}
              options={versions.map((asset) => (
                <DownloadButton
                  key={`download-${asset.version}`}
                  icon={<IconMusicNote />}
                  label={capitalizeString(asset.version!)}
                  assetPath={asset.path!}
                  track={track}
                />
              ))}
              requestClose={this.state.downloadMenuOpen === false}
              disableOnClickOutside={this.state.downloadMenuOpen === false}
              onToggle={this.onToggleDropdownMenu}
            />
          ) : (
              <DownloadButton assetPath={downloadableAsset.path!} track={track} {...classes('action')} />
            )}
          {/*<Button label="Share" icon={<IconShare />} {...classes('action') } />*/}
          {canLicenseTrack(track) ?
            <AddToCartButton
              label={<PriceTag amount={track.price!} label="License for" />}
              item={track}
              size="small-medium"
              variant="pill"
              {...classes('button-license')}
            /> :
            <SessionLicenseBuilderButton
              colorScheme="accent"
              label="License this track"
              icon={<IconAddToCart />}
              size="small-medium"
              variant="pill"
              {...classes('button-license')}
            />
          }
        </div>
      </div>
    );
  }

  renderVersionsLicense() {
    const {
      currentVersionPlaying,
      pausePlayer,
      playTrackVersion,
      track,
    } = this.props;

    const {
      selectedVersion,
    } = this.state;

    return (
      <div {...classes('versions-and-license')}>
        <div {...classes('versions')}>
          <Heading is="h6" {...classes('versions__title')}>Versions</Heading>
          <div className="buttons-group">
            {this.versions.map(({ id, version }) => {
              const isActive = selectedVersion === id;
              const extraClasses = {
                'is-active': isActive,
              };
              const buttonProps: Partial<ButtonProps> = {
                label: capitalizeString(version!),
                ...classes('version-button', '', extraClasses),
              };
              if (!isActive) {
                buttonProps.onClick = () => this.onClickVersion(id);
              }
              return <Button key={id} {...buttonProps} />;
            })}
          </div>
          <div {...classes('versions__player')}>
            <CardPlayButton
              isPlaying={!!currentVersionPlaying}
              onClick={() => {
                currentVersionPlaying === selectedVersion ?
                  pausePlayer()
                  : playTrackVersion(selectedVersion);
              }}
              {...classes('versions__player__play-button')}
            />
            <WaveformPlaceholder {...classes('versions__player__waveform')} />
          </div>
        </div>
        {!!track.price && track.price > 0 ? (
          <div {...classes('license')}>
            <div {...classes('license__pricing')}>
              <PriceTag amount={track.price!} size="large" {...classes('price')} />
              <AddToCartButton label="License this track" item={track} />
            </div>
            <div {...classes('license__details')}>
              <Span variant="description" text="Prices for:" textColor={bw7} />
              <div {...classes('usages')}>
                {this.props.licenseUsages.map((usage) => (
                  <Button
                    key={`license-usage-${usage.medium.id}`}
                    {...classes('usage-button')}
                    label={<Span text={usageToString(usage)} variant="description" size="large" />}
                    variant="text"
                    size="small"
                    icon={<IconEdit />}
                    iconPosition="right"
                  />
                ))}
                <Button
                  {...classes('add-usage-button')}
                  label="Add More Media"
                  variant="text"
                  icon={<IconAddCircle />}
                />
              </div>
            </div>
          </div>
        ) : (
            <div {...classes('license', 'no-terms')}>
              <Heading is="h4" {...classes('license__title')}>Want a price for this track?</Heading>
              <Button
                label="Build Your License"
                colorScheme="accent"
                icon={<IconTerms />}
              />
            </div>
          )}
      </div>
    );
  }

  renderTermsByTaxonomy(terms: GQL.ITaxonomyTerm[]) {
    return terms.map(({ id, name }) => (
      <Tag
        key={id}
        id={id}
        label={name!}
        colorScheme="white"
        onClick={() => this.props.addSearchFilter({name: name!})}
      />
    ));
  }

  render() {
    const { track } = this.props;

    const tempos = track.tempos || defaultList;
    const moods = getTermsByTaxonomy(TaxonomyType.Moods, this.terms);
    const genres = getTermsByTaxonomy(TaxonomyType.Genres, this.terms);
    const instruments = getTermsByTaxonomy(TaxonomyType.Instruments, this.terms);
    const languages = getTermsByTaxonomy(TaxonomyType.Languages, this.terms);
    const vocals = getTermsByTaxonomy(TaxonomyType.Vocals, this.terms);
    const tags = getTermsByTaxonomy(TaxonomyType.Tags, this.terms);
    const defaultTrackAsset = getDefaultTrackAsset(track.assets!);
    const featuredCollections = (track.collections!.edges || defaultList).map(({ node }) => node!);
    const duration = defaultTrackAsset && defaultTrackAsset.metadata ?
      getFormattedTime(defaultTrackAsset.metadata.duration)
      : null;
    const hasAttributes = !!track.stems_available || !!track.customizable;

    return (
      <Page id="track" layout="loose">
        <TrackHelmet track={track} />
        <section className="page__main">
          {this.renderHero()}
          {this.renderHeader()}
          <div {...classes('content')}>
            <div {...classes('content__wrapper')}>
              <div {...classes('content__column', 'metadata')}>
                <Heading is="h3">Info</Heading>
                {/*<div {...classes('tags-group') } data-title="ID">
                  <Tag id="_" label={`${track.internal_id!}`} />
                </div>
                <div {...classes('tags-group') } data-title="Catalog">
                  <Tag id="_" label={track.artist!.name!} />
                </div>*/}
                <div {...classes('tags-group')} data-title="Tempo">
                  {tempos.map(({ id, name, value }) => (
                    <Tag
                      key={id}
                      id={id}
                      label={name!}
                      colorScheme="white"
                      onClick={() => this.onSetTempoFilter(value!)}
                    />
                  ))}
                </div>
                {duration &&
                  <div {...classes('tags-group')} data-title="Duration">
                    <Tag id="_" label={duration} colorScheme="white" />
                  </div>
                }
                {hasAttributes &&
                  <div {...classes('tags-group')} data-title="Attributes">
                    {track.stems_available &&
                      <Tag id="stems-available" label="Stems available" colorScheme="white" />
                    }
                    {track.customizable &&
                      <Tag id="customizable" label="Customizable" colorScheme="white" />
                    }
                  </div>
                }
              </div>
              <div {...classes('content__column', 'facets')}>
                <Heading is="h3">Tags</Heading>
                {!!moods.length && (
                  <div {...classes('tags-group')} data-title="Moods">
                    {this.renderTermsByTaxonomy(moods)}
                  </div>
                )}
                {!!genres.length && (
                  <div {...classes('tags-group')} data-title="Genres">
                    {this.renderTermsByTaxonomy(genres)}
                  </div>
                )}
                {!!instruments.length && (
                  <div {...classes('tags-group')} data-title="Instruments">
                    {this.renderTermsByTaxonomy(instruments)}
                  </div>
                )}
                {!!languages.length && (
                  <div {...classes('tags-group')} data-title="Languages">
                    {this.renderTermsByTaxonomy(languages)}
                  </div>
                )}
                {!!vocals.length && (
                  <div {...classes('tags-group')} data-title="Vocals">
                    {this.renderTermsByTaxonomy(vocals)}
                  </div>
                )}
                {!!tags.length && (
                  <div {...classes('tags-group')} data-title="Tags">
                    {this.renderTermsByTaxonomy(tags)}
                  </div>
                )}
              </div>
              <div {...classes('content__column', 'other-tracks')}>
                <TrackListQuery
                  actionSource="track_page"
                  cardProps={{
                    backgroundColor: 'bw3',
                    size: 'small',
                    options: ['PriceElement', 'AddToProject', 'ShowDetails'],
                  }}
                  excludeSearchResults={[]}
                  searchQuery={{
                    count: 5,
                    filters: {
                      similarity: {
                        track_id: track.id,
                        feature: GQL.TrackSimilarityFeature.Default,
                      },
                    },
                  }}
                  title="More like this"
                  {...classes('from-same-artist')}
                />
                <TrackListQuery
                  actionSource="track_page"
                  cardProps={{
                    backgroundColor: 'bw3',
                    size: 'small',
                    options: ['PriceElement', 'AddToProject', 'ShowDetails'],
                  }}
                  excludeSearchResults={[track.id]}
                  searchQuery={{
                    count: 5,
                    filters: {
                      artist_slug: track.artist!.slug!,
                    },
                  }}
                  title="More from the same artist"
                  {...classes('from-same-artist')}
                />
                <LinkButtonWithLicense
                  to={ArtistPagePath(track.artist!.slug!)}
                  external
                  label="Go to Artist"
                  icon={<IconArrowRight />}
                  iconPosition="right"
                  colorScheme="white"
                  className="show-all-tracks-button"
                />
              </div>
            </div>
            {!!featuredCollections.length &&
              <div {...classes('content__wrapper')}>
                <div {...classes('content__column', 'collections')}>
                  <Heading is="h3">Featured in Collections</Heading>
                  <Collections data={featuredCollections} gridView hideTitle />
                </div>
              </div>
            }
          </div>
        </section>
      </Page>
    );
  }
}

const TrackPageWithLicenseBar = withLicenseBar(TrackPage);

const ConnectedTrackPage = connect<IOwnStateProps, IOwnDispatchProps, IOwnProps & QueryStringContextProps, IStoreState>(
  ({ license, player }, ownProps) => {
    const {
      currentTrack,
      isTrackPlaying,
    } = player;

    let currentTrackPlaying = '';
    let currentVersionPlaying = '';

    if (isTrackPlaying && currentTrack && currentTrack.trackId === ownProps.track.id) {
      currentTrackPlaying = currentTrack.trackId;
      currentVersionPlaying = currentTrack.versionId;
    }

    return {
      currentTrackPlaying,
      currentVersionPlaying,
      licenseUsages: license.usages,
    };
  },
  (dispatch) => ({
    pausePlayer: () => {
      dispatch(playerActions.pause());
    },
    playTrackVersion: (trackId) => {
      dispatch(playerActions.playTrackFromQueue({ trackId }));
    },
    setPlayerQueue: (tracks: IPlayerTrack[]) => {
      dispatch(playerActions.addTracksToQueue({ tracks }));
    },
  }),
)(TrackPageWithLicenseBar);

const TrackPageWithQueryStringContext: React.SFC<IOwnProps> = (props) => (
  <QueryStringContext.Consumer>
    {({ addSearchFilter, addTempoFilter }) => (
      <ConnectedTrackPage
        addSearchFilter={addSearchFilter}
        addTempoFilter={addTempoFilter}
        {...props}
      />
    )}
  </QueryStringContext.Consumer>
);

const RelayTrackPage = createFragmentContainer(
  TrackPageWithQueryStringContext,
  TrackFragment,
);

export default RelayTrackPage;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/Track/index.tsx